import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Pause, Play, RefreshCw } from 'lucide-react';
import { api } from '../api/client';
import { Badge, ReachDot, EmptyState, ErrorState, Skeleton, useFetch } from '../components/ui';
import { TimeseriesChart, BarList } from '../components/charts';

/* ================================================================== */
/* Types — mirrors the box's own server_monitor.php JSON, verbatim     */
/* ================================================================== */

interface KeyDispo {
  code: string;
  label: string;
  color: string;
  count: number;
}

/** A recorded answered call surfaced by the voicemail engine (voip plugin). */
interface VmRecentItem {
  gateway: string;
  line: number;
  transcript: string;
  at: number;
}

/** A GoIP gateway's detailed status + its last call, for the CDR panel. */
interface GoipCdrRow {
  id: string;
  name: string;
  peer: string | null;
  reachable: boolean;
  status: { total: number; connected: number; active: number; idle: number; failing: number; carriers: string[] } | null;
  lastCdr: {
    startedAt: number;
    src: string | null;
    dst: string | null;
    direction: string | null;
    disposition: string | null;
    durationSec: number;
    billsec: number;
    hangupCause: string | null;
  } | null;
}

function clock(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}

function agoLabel(at: number): string {
  const s = Math.max(0, Math.round((Date.now() - at) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  return `${Math.round(m / 60)}h ago`;
}

interface Dispo {
  code: string;
  label: string;
  count: number;
}

interface AgentDispo {
  code: string;
  count: number;
}

interface AgentRow {
  user: string;
  name: string;
  status: string;
  campaign: string | null;
  calls_today: number;
  session_calls: number | null;
  state_sec: number | null;
  idle_sec: number | null;
  idle_today: number;
  dispos: AgentDispo[];
}

interface Dialable {
  campaign: string;
  count: number;
  total: number;
  pct: number | null;
}

interface MonitorStats {
  ts: number;
  cpu_pct: number;
  ncpu: number;
  ast_pcpu: number;
  ast_pct_total: number;
  mem_pct: number;
  mem_used_mb: number;
  mem_total_mb: number;
  swap_used_mb: number;
  load1: number;
  active_calls: number;
  live_channels: number;
  placing: number;
  dial_in_progress: number;
  dead_awaiting_cleanup: number;
  queued_agi: number;
  carriers: Record<string, number>;
  carriers_placed: Record<string, number>;
  carrier_names: string[];
  dispos: Dispo[];
  dispos_total: number;
  key_dispos: KeyDispo[];
  dispo_campaigns: string[];
  dialable: Dialable[];
  agents_total: number;
  agents_incall: number;
  agents_ready: number;
  agents_paused: number;
  agents_idle_avg: number;
  agents_list: AgentRow[];
  mysql_qps: number;
  mysql_conn: number;
  mysql_running: number;
  mysql_slow: number;
  mysql_cpu: number;
  mysql_mem_mb: number;
}

interface HistoryPoint {
  ts: number;
  cpu: number;
  astpct: number;
  load: number;
  memp: number;
  calls: number;
  chan: number;
  agents: number;
}

const REFRESH_OPTIONS = [
  { sec: 2, label: '2s' },
  { sec: 5, label: '5s' },
  { sec: 10, label: '10s' },
  { sec: 30, label: '30s' },
];
const WINDOW_OPTIONS = [
  { min: 5, label: '5 min' },
  { min: 15, label: '15 min' },
  { min: 60, label: '1 hr' },
];

/** Coerce a possibly-missing numeric field to a finite number.
 *
 * History-seeded samples carry only a subset of a live snapshot's fields (see
 * the preload effect), so a stat read like `latest.mysql_cpu` can be undefined
 * for the first paint. Reading `.toFixed` off that crashed the whole page to
 * blank; this keeps it at 0 until the first live tick fills the real value. */
const n = (v: number | null | undefined): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

const AGENT_STATUS_INTENT: Record<string, 'good' | 'warn' | 'bad' | 'neutral' | 'info'> = {
  INCALL: 'bad',
  QUEUE: 'warn',
  READY: 'good',
  PAUSED: 'warn',
  CLOSER: 'info',
  OFFLINE: 'neutral',
};

/* ================================================================== */
/* Page                                                                */
/* ================================================================== */

export function ViciDialMonitor() {
  const { id } = useParams<{ id: string }>();

  const [refreshSec, setRefreshSec] = useState(2);
  const [windowMin, setWindowMin] = useState(5);
  const [paused, setPaused] = useState(false);
  const [campaign, setCampaign] = useState('');
  const [idleThresh, setIdleThresh] = useState(60);
  const [leadsThresh, setLeadsThresh] = useState(10);

  const [samples, setSamples] = useState<MonitorStats[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [recorded, setRecorded] = useState<VmRecentItem[]>([]);
  const [goipRows, setGoipRows] = useState<GoipCdrRow[]>([]);
  const seededWindow = useRef<number | null>(null);

  const { data: resource } = useFetch(() => api.get<{ id: string; name: string; kind: string; health: string }>(`/api/resources/${id}`), [id]);

  const maxPoints = Math.max(10, Math.ceil((windowMin * 60) / refreshSec));

  // Preload from the box's own stored history so the charts aren't empty on
  // first paint — reseeded whenever the window changes, since a wider window
  // needs more back-fill than the live buffer alone has accumulated yet.
  useEffect(() => {
    if (!id || seededWindow.current === windowMin) return;
    seededWindow.current = windowMin;
    void (async () => {
      try {
        const hist = await api.get<HistoryPoint[]>(`/api/plugins/voip/dialer/${id}/monitor`, {
          action: 'history',
          mins: windowMin,
        });
        // The history rows carry a narrower field set than a live snapshot —
        // fine, every chart below only reads the fields it actually has.
        const seeded = hist.slice(-maxPoints).map(
          (h): MonitorStats =>
            ({
              ts: h.ts * 1000,
              cpu_pct: h.cpu,
              ast_pct_total: h.astpct,
              load1: h.load,
              mem_pct: h.memp,
              active_calls: h.calls,
              live_channels: h.chan,
              agents_total: h.agents,
            }) as MonitorStats,
        );
        setSamples((current) => (current.length === 0 ? seeded : current));
      } catch {
        // No history yet (fresh box, or the table isn't populated) — the live
        // buffer will fill in from here regardless.
      }
    })();
  }, [id, windowMin, maxPoints]);

  useEffect(() => {
    if (!id || paused) return;
    let cancelled = false;

    const tick = async () => {
      try {
        const stats = await api.get<MonitorStats>(`/api/plugins/voip/dialer/${id}/monitor`, {
          action: 'stats',
          ...(campaign ? { campaign } : {}),
        });
        if (cancelled) return;
        setError(null);
        setLoading(false);
        setSamples((current) => [...current, { ...stats, ts: stats.ts * 1000 }].slice(-maxPoints));
      } catch (err) {
        if (cancelled) return;
        setLoading(false);
        setError(err instanceof Error ? err.message : 'Could not reach the ViciDial box');
      }
    };

    void tick();
    const timer = window.setInterval(() => void tick(), refreshSec * 1000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [id, refreshSec, paused, campaign, maxPoints]);

  // Recorded answered calls heard on the GoIP gateways that dial through this PBX
  // — a slow poll, independent of the live stats refresh.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get<{ items: VmRecentItem[] }>(`/api/plugins/voip/dialer/${id}/vm-recent`);
        if (!cancelled) setRecorded(r.items);
      } catch {
        /* keep the last view */
      }
    };
    void tick();
    const timer = window.setInterval(() => void tick(), 6000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [id]);

  // GoIP gateways' detailed status + last call. Slow poll (hits each device).
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get<{ gateways: GoipCdrRow[] }>(`/api/plugins/voip/dialer/${id}/goip-cdr`);
        if (!cancelled) setGoipRows(r.gateways);
      } catch {
        /* keep the last view */
      }
    };
    void tick();
    const timer = window.setInterval(() => void tick(), 10000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [id]);

  const latest = samples[samples.length - 1];
  const idleAlerting = !!latest && n(latest.agents_idle_avg) >= idleThresh && idleThresh > 0;

  const series = (pick: (s: MonitorStats) => number | null | undefined, name: string, intent?: 'good' | 'warn' | 'bad') => ({
    name,
    intent,
    points: samples.map((s) => [s.ts, pick(s) ?? null] as [number, number | null]),
  });

  if (loading && samples.length === 0 && !error) {
    return (
      <div className="col" style={{ gap: 14 }}>
        <Skeleton height={90} />
        <Skeleton height={320} />
      </div>
    );
  }
  if (error && samples.length === 0) return <ErrorState error={error} onRetry={() => setError(null)} />;
  if (!latest) return <EmptyState title="No data yet" text="Waiting for the first sample from the box." />;

  const carrierNames = latest.carrier_names ?? [];
  const carrierSeries = carrierNames.map((name) => ({
    name,
    points: samples.map((s) => [s.ts, s.carriers?.[name] ?? 0] as [number, number | null]),
  }));

  return (
    <div className="col" style={{ gap: 14 }}>
      <div className="page-header">
        <div className="grow">
          <div className="page-subtitle" style={{ marginBottom: 2 }}>
            <Link to={`/resources/${id}`}>
              {resource && <ReachDot health={resource.health} />}
              {resource?.name ?? 'Resource'}
            </Link>
          </div>
          <h1 className="page-title">Server Monitor</h1>
          <div className="page-subtitle">
            {error ? (
              <span style={{ color: 'var(--itr-critical)' }}>{error} — showing last good sample</span>
            ) : (
              <>
                <span className="health" data-health="up" style={{ ['--health-color' as string]: 'var(--itr-up)' }}>
                  <span className="health-dot" />
                </span>{' '}
                live — {new Date(latest.ts).toLocaleTimeString()}
              </>
            )}
          </div>
        </div>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <label className="row" style={{ gap: 5, fontSize: 12.5 }}>
            Refresh
            <select className="input" style={{ padding: '4px 8px' }} value={refreshSec} onChange={(e) => setRefreshSec(Number(e.target.value))}>
              {REFRESH_OPTIONS.map((o) => (
                <option key={o.sec} value={o.sec}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="row" style={{ gap: 5, fontSize: 12.5 }}>
            Window
            <select className="input" style={{ padding: '4px 8px' }} value={windowMin} onChange={(e) => setWindowMin(Number(e.target.value))}>
              {WINDOW_OPTIONS.map((o) => (
                <option key={o.min} value={o.min}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="row" style={{ gap: 5, fontSize: 12.5 }} title="Pulse the Agents card when average idle time (seconds) crosses this">
            Idle alert
            <input
              className="input"
              style={{ width: 56, padding: '4px 8px' }}
              type="number"
              min={0}
              value={idleThresh}
              onChange={(e) => setIdleThresh(Number(e.target.value))}
            />
          </label>
          <label className="row" style={{ gap: 5, fontSize: 12.5 }} title="Pulse a campaign's leads bar when dialable leads drop below this %">
            Leads alert
            <input
              className="input"
              style={{ width: 56, padding: '4px 8px' }}
              type="number"
              min={0}
              value={leadsThresh}
              onChange={(e) => setLeadsThresh(Number(e.target.value))}
            />
          </label>
          <button className="btn btn-sm" onClick={() => setPaused((p) => !p)}>
            {paused ? (
              <>
                <Play size={13} /> Resume
              </>
            ) : (
              <>
                <Pause size={13} /> Pause
              </>
            )}
          </button>
        </div>
      </div>

      {recorded.length > 0 && (
        <div className="card" style={{ borderColor: 'var(--itr-warning, #b8860b)' }}>
          <div className="card-header">
            <span className="card-title">🎙 Recorded answered calls</span>
            <span className="grow" />
            <Badge intent="warn">{recorded.length}</Badge>
          </div>
          <div className="card-body" style={{ display: 'grid', gap: 8, maxHeight: 320, overflowY: 'auto' }}>
            {recorded.map((r, i) => (
              <div
                key={`${r.gateway}-${r.line}-${r.at}`}
                style={{
                  display: 'grid',
                  gap: 3,
                  padding: '8px 10px',
                  borderRadius: 8,
                  background: i === 0 ? 'rgba(184, 134, 11, 0.12)' : 'var(--itr-surface-2, rgba(255,255,255,0.03))',
                }}
              >
                <div className="row" style={{ gap: 8, alignItems: 'baseline', fontSize: 12 }}>
                  <Badge intent="warn">Recorded Message</Badge>
                  <strong>{r.gateway}</strong>
                  <span className="subtle">line {r.line}</span>
                  <span className="grow" />
                  <span className="subtle">{agoLabel(r.at)}</span>
                </div>
                <div dir="auto" style={{ fontSize: 13, lineHeight: 1.4 }}>
                  {r.transcript}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {goipRows.length > 0 && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">📟 GoIP gateways — status &amp; last call</span>
            <span className="grow" />
            <Badge intent="info">{goipRows.length}</Badge>
          </div>
          <div className="card-body" style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Gateway</th>
                  <th>Status</th>
                  <th>Lines</th>
                  <th>Last call</th>
                </tr>
              </thead>
              <tbody>
                {goipRows.map((g) => (
                  <tr key={g.id}>
                    <td style={{ fontWeight: 550 }}>
                      {g.name}
                      {g.peer && (
                        <span className="subtle" style={{ fontSize: 11, marginLeft: 6 }}>
                          {g.peer}
                        </span>
                      )}
                    </td>
                    <td>
                      {!g.reachable ? (
                        <Badge intent="bad">unreachable</Badge>
                      ) : g.status ? (
                        <span className="row" style={{ gap: 6, fontSize: 12, flexWrap: 'wrap' }}>
                          <Badge intent="good">{g.status.active} active</Badge>
                          {g.status.failing > 0 && <Badge intent="warn">{g.status.failing} failing</Badge>}
                          {g.status.carriers.length > 0 && <span className="subtle">{g.status.carriers.join(', ')}</span>}
                        </span>
                      ) : (
                        <span className="subtle">—</span>
                      )}
                    </td>
                    <td className="subtle" style={{ fontSize: 12 }}>
                      {g.status ? `${g.status.connected} conn · ${g.status.idle} idle · ${g.status.total} total` : '—'}
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {g.lastCdr ? (
                        <div className="col" style={{ gap: 1 }}>
                          <span>
                            <strong>{g.lastCdr.dst ?? g.lastCdr.src ?? '—'}</strong>{' '}
                            <Badge intent={/ANSWER/i.test(g.lastCdr.disposition ?? '') ? 'good' : 'warn'}>{g.lastCdr.disposition ?? '?'}</Badge>
                          </span>
                          <span className="subtle">
                            {clock(g.lastCdr.billsec || g.lastCdr.durationSec)} · {agoLabel(g.lastCdr.startedAt)}
                            {g.lastCdr.hangupCause ? ` · ${g.lastCdr.hangupCause}` : ''}
                          </span>
                        </div>
                      ) : (
                        <span className="subtle">{g.peer ? 'no calls recorded' : 'set trunkPeer to link CDRs'}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="stat-strip">
        {latest.key_dispos?.map((k) => (
          <div key={k.code} className="stat-chip">
            <div className="stat-chip-label">{k.label}</div>
            <div className="stat-chip-value" style={{ color: k.color }}>
              {k.count}
            </div>
          </div>
        ))}
        <div className="stat-chip">
          <div className="stat-chip-label">CPU</div>
          <div className="stat-chip-value" style={{ color: n(latest.cpu_pct) > 85 ? 'var(--itr-critical)' : undefined }}>
            {n(latest.cpu_pct).toFixed(1)}%
          </div>
          <div className="stat-chip-sub">{n(latest.ncpu)} cores</div>
        </div>
        <div className="stat-chip">
          <div className="stat-chip-label">Asterisk CPU</div>
          <div className="stat-chip-value">{n(latest.ast_pct_total).toFixed(1)}%</div>
          <div className="stat-chip-sub">≈{(n(latest.ast_pcpu) / 100).toFixed(1)} cores</div>
        </div>
        <div className="stat-chip">
          <div className="stat-chip-label">MySQL CPU</div>
          <div className="stat-chip-value">{n(latest.mysql_cpu).toFixed(1)}%</div>
          <div className="stat-chip-sub">{n(latest.mysql_qps)} q/s</div>
        </div>
        <div className="stat-chip">
          <div className="stat-chip-label">Memory</div>
          <div className="stat-chip-value" style={{ color: n(latest.mem_pct) > 90 ? 'var(--itr-critical)' : undefined }}>
            {n(latest.mem_pct).toFixed(1)}%
          </div>
          <div className="stat-chip-sub">
            {(n(latest.mem_used_mb) / 1024).toFixed(1)} / {(n(latest.mem_total_mb) / 1024).toFixed(1)} GB
          </div>
        </div>
        <div className="stat-chip">
          <div className="stat-chip-label">Load (1m)</div>
          <div className="stat-chip-value">{n(latest.load1).toFixed(2)}</div>
          <div className="stat-chip-sub">per core {(n(latest.load1) / Math.max(1, n(latest.ncpu))).toFixed(2)}</div>
        </div>
        <div className="stat-chip">
          <div className="stat-chip-label">Active Calls</div>
          <div className="stat-chip-value">{n(latest.active_calls)}</div>
          <div className="stat-chip-sub">{n(latest.live_channels)} channels</div>
        </div>
        <div className="stat-chip">
          <div className="stat-chip-label">Placing</div>
          <div className="stat-chip-value">{n(latest.placing)}</div>
          <div className="stat-chip-sub">
            {n(latest.dial_in_progress)} on trunk · {n(latest.queued_agi)} queued/AGI
          </div>
        </div>
        <div className={`stat-chip${idleAlerting ? ' alerting' : ''}`}>
          <div className="stat-chip-label">Agents</div>
          <div className="stat-chip-value">{n(latest.agents_total)}</div>
          <div className="stat-chip-sub">
            {n(latest.agents_ready)} ready · {n(latest.agents_paused)} paused · idle {n(latest.agents_idle_avg)}s
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 1.2fr) minmax(300px, 1fr)', gap: 14 }}>
        <div className="card">
          <div className="card-header">
            <span className="card-title">Today's Dispositions</span>
            <span className="grow" />
            <span className="subtle" style={{ fontSize: 12 }}>
              {n(latest.dispos_total).toLocaleString()} leads today
            </span>
          </div>
          <div className="card-body">
            <select className="input" style={{ marginBottom: 10, fontSize: 12.5 }} value={campaign} onChange={(e) => setCampaign(e.target.value)}>
              <option value="">All campaigns</option>
              {latest.dispo_campaigns?.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <BarList bars={(latest.dispos ?? []).map((d) => ({ label: d.label, value: d.count }))} />
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <span className="card-title">Today's Agents</span>
            <span className="grow" />
            <span className="subtle" style={{ fontSize: 12 }}>
              {latest.agents_list?.length ?? 0} today
            </span>
          </div>
          <div style={{ padding: '4px 0', maxHeight: 340, overflowY: 'auto' }}>
            {(latest.agents_list ?? []).map((a) => (
              <div key={a.user} className="row" style={{ padding: '7px 16px', gap: 10, fontSize: 12.5 }}>
                <Badge intent={AGENT_STATUS_INTENT[a.status] ?? 'neutral'}>{a.status}</Badge>
                <span className="grow truncate">{a.name}</span>
                <span className="subtle nums" style={{ fontSize: 11.5 }}>
                  {a.calls_today} calls
                  {a.idle_sec !== null && a.idle_sec !== undefined ? ` · idle ${a.idle_sec}s` : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 14 }}>
        <ChartCard title="Agents (in-call / ready / paused)">
          <TimeseriesChart
            series={[
              series((s) => s.agents_incall, 'In-call', 'bad'),
              series((s) => s.agents_ready, 'Ready', 'good'),
              series((s) => s.agents_paused, 'Paused', 'warn'),
            ]}
            height={220}
          />
        </ChartCard>

        <ChartCard title="Calls: Active / Placing">
          <TimeseriesChart
            series={[series((s) => s.active_calls, 'Active'), series((s) => s.placing, 'Placing'), series((s) => s.queued_agi, 'Queued/AGI')]}
            height={220}
          />
        </ChartCard>

        <ChartCard title="Live Channels">
          <TimeseriesChart series={[series((s) => s.live_channels, 'Live channels')]} height={220} />
        </ChartCard>

        <ChartCard title="CPU % (system vs Asterisk)">
          <TimeseriesChart
            series={[series((s) => s.cpu_pct, 'CPU %'), series((s) => s.ast_pct_total, 'Asterisk % of total')]}
            unit="percent"
            height={220}
          />
        </ChartCard>

        <ChartCard title="Memory %">
          <TimeseriesChart series={[series((s) => s.mem_pct, 'Mem %', 'warn')]} unit="percent" height={220} />
        </ChartCard>

        <ChartCard title="Active Calls per Carrier">
          <TimeseriesChart series={carrierSeries} height={220} />
        </ChartCard>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 14 }}>
        <div className="card">
          <div className="card-header">
            <span className="card-title">Dialable Leads per Campaign</span>
            <span className="grow" />
            <span className="subtle" style={{ fontSize: 12 }}>
              {(latest.dialable ?? []).reduce((n, d) => n + d.count, 0).toLocaleString()} total
            </span>
          </div>
          <div className="card-body">
            <BarList
              bars={(latest.dialable ?? []).map((d) => ({
                label: `${d.campaign}${d.pct !== null ? ` · ${d.pct}%` : ''}`,
                value: d.count,
                intent: d.pct !== null && d.pct < leadsThresh ? 'bad' : undefined,
              }))}
            />
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <span className="card-title">Calls per Carrier (active vs placed)</span>
          </div>
          <div className="card-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <div className="subtle" style={{ fontSize: 11, marginBottom: 6 }}>
                Active
              </div>
              <BarList bars={carrierNames.map((c) => ({ label: c, value: latest.carriers?.[c] ?? 0, intent: 'good' as const }))} />
            </div>
            <div>
              <div className="subtle" style={{ fontSize: 11, marginBottom: 6 }}>
                Placed
              </div>
              <BarList bars={carrierNames.map((c) => ({ label: c, value: latest.carriers_placed?.[c] ?? 0 }))} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title">{title}</span>
      </div>
      <div className="card-body" style={{ height: 220 }}>
        {children}
      </div>
    </div>
  );
}
